import {ChangeDetectionStrategy, Component, ElementRef, Inject, Injectable, Signal, signal} from "@angular/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogContent, MatDialogRef} from "@angular/material/dialog";
import {EnkelGruppeModell, RegistrerBesvarelseValideringsfeil, RegistrerRespondentsBesvarelseSpesifikasjon, RespondentBesvarelseModell, RespondentId, SpørreundersøkelseId, SpørreundersøkelseModell, SpørreundersøkelseRessursClient, ValideringsresultatModell} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {ReactiveFormsModule} from "@angular/forms";
import {lyttTilEsc} from "felles-ng2/støttefunksjoner/tastaturhendelser";
import {SpørreundersøkelseBesvarelseSkjemaKomponent} from "../../komponenter/spørreundersøkelse-besvarelse-skjema/spørreundersøkelse-besvarelse-skjema";
import {SpørreundersøkelseSpørsmålSkjemaKomponent, SpørsmålSkjemaVerdi, Svarverdi} from "../../komponenter/spørreundersøkelse-spørsmål-skjema/spørreundersøkelse-spørsmål-skjema";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {MatToolbar} from "@angular/material/toolbar";
import {SubmitButtonKomponent} from "web-ng2/delt/komponenter/skjema/submit-button/submit-button";
import {TranslateModule} from "@ngx-translate/core";
import {asynkArbeidPågårSignal, standardMinimumVarighetKonfig} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {toSignal} from "@angular/core/rxjs-interop";
import {map} from "rxjs/operators";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {erSpørsmålSomKanBesvares} from "felles-ng2/modell/spørreundersøkelseModellhjelpere";
import {tomSpørreundersøkelseModell} from "felles-ng2/modell/tommeModeller";
import {nil} from "felles-ng2/typer/nullish";
import {ZonedDateTimeSomPlainDatePipe} from "felles-ng2/pipes/ZonedDateTimeSomPlainDatePipe";
import {FormaterPlainDatePipe} from "felles-ng2/pipes/FormaterPlainDatePipe";
import {Navigasjon} from "web-ng2/tjenester/Navigasjon";
import {BrukerkontekstTilbyder} from "felles-ng2/tjenester/BrukerkontekstTilbyder";
import {HttpErrorResponse} from "@angular/common/http";
import {HttpResponskoder} from "felles-ng2/infrastruktur/http/httpResponskoder";
import {VisDersomIkkeScrolletTilBunnDirektiv} from "web-ng2/delt/direktiver/visDersomIkkeScrolletTilBunn/visDersomIkkeScrolletTilBunn";
import {scrollTilSenter} from "web-ng2/delt/dom/scrollIntoView";
import {animerBlinkEnGangFadeUt} from "web-ng2/delt/animasjoner";
import {AnimationBuilder} from "@angular/animations";
import {TipsKomponent} from "web-ng2/delt/komponenter/tips/tips";
import {i18n, I18nTekst} from "felles-ng2/tjenester/i18n/Oversettelser";
import {MeldingTilBrukerNg2} from "felles-ng2/tjenester/MeldingTilBrukerNg2";

const debug = initDebugger('SpørreundersøkelseBesvarelseDialogKomponent');


/** Sann dersom bruker besvarte skjema, false dersom bruker avbrøt */
export type SpørreundersøkelseBesvarelsedialogRespons = boolean;

/**
 * Dialog for besvarelse av spørreundersøkelse
 */
@Component({
    selector: 'spoerreundersoekelse-besvarelse-dialog',
    templateUrl: 'spørreundersøkelse-besvarelse-dialog.html',
    styleUrl: 'spørreundersøkelse-besvarelse-dialog.scss',
    imports: [
        MatButton,
        MatDialogActions,
        MatDialogContent,
        MatIcon,
        MatIconButton,
        MatToolbar,
        ReactiveFormsModule,
        SubmitButtonKomponent,
        TranslateModule,
        SpørreundersøkelseBesvarelseSkjemaKomponent,
        ZonedDateTimeSomPlainDatePipe,
        FormaterPlainDatePipe,
        VisDersomIkkeScrolletTilBunnDirektiv,
        TipsKomponent
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
class SpørreundersøkelseBesvarelseDialogKomponent {

    /** Viser skjema for å besvare undersøkelse. Dersom respondentId er angitt populeres skjema med vedkommendes eksisterende besvarelse. */
    static vis(dialog: MatDialog, spørreundersøkelseId: SpørreundersøkelseId, respondentId: RespondentId | null = null): MatDialogRef<SpørreundersøkelseBesvarelseDialogKomponent, SpørreundersøkelseBesvarelsedialogRespons> {
        return dialog.open(SpørreundersøkelseBesvarelseDialogKomponent, {
            data: {spørreundersøkelseId, respondentId} as DialogParametre,
            disableClose: true,
            ariaLabelledBy: 'spørreundersøkelse-besvarelse-dialog-tittel',
            height: '75vh',//.mat-mdc-dialog-content er def med 'max-height: 65vh', så mer enn 75vh vil ikke fungere uten at .mat-mdc-dialog-content overstyres
            width: '80vw',
            maxWidth: '1200px'
        });
    }

    skjema = SpørreundersøkelseBesvarelseSkjemaKomponent.opprettSkjema();
    spoerreundersoekelse = signal<SpørreundersøkelseModell>(tomSpørreundersøkelseModell())
    antallUbesvarteSpoersmaal: Signal<number>;
    gruppe = signal<EnkelGruppeModell | null>(null);
    modus = signal<'skjema' | 'kvittering' | 'stengt'>('skjema');
    laster = asynkArbeidPågårSignal();
    lagrer = asynkArbeidPågårSignal(standardMinimumVarighetKonfig());

    private blinkSpørsmålsrad = this.animationBuilder.build(animerBlinkEnGangFadeUt());

    constructor(@Inject(MAT_DIALOG_DATA) parametre: DialogParametre,
                private avhengigheter: SpørreundersøkelseBesvarelsedialogKomponentAvhengigheter,
                private el: ElementRef<HTMLElement>,
                private dialogRef: MatDialogRef<SpørreundersøkelseBesvarelseDialogKomponent, SpørreundersøkelseBesvarelsedialogRespons>,
                private animationBuilder: AnimationBuilder,
                private kontekst: BrukerkontekstTilbyder,
                private navigasjon: Navigasjon,
                private meldingTilBruker: MeldingTilBrukerNg2) {
        //dialog er konf med disableClose:true for å unngå at klikk utenfor dialog lukker denne, ønsker fortsatt at esc skal lukke
        lyttTilEsc(() => this.avbryt());

        this.initSkjema(parametre.spørreundersøkelseId, parametre.respondentId);

        this.antallUbesvarteSpoersmaal = toSignal(this.skjema.valueChanges.pipe(
          map(svarsamling => {
              return svarsamling
                .filter(innslag => erSpørsmålSomKanBesvares(innslag.spoersmaal!))
                .filter(innslag => !SpørreundersøkelseSpørsmålSkjemaKomponent.erBesvart(innslag.svar))
                .length;
          })
        ), {initialValue: 0});
    }

    tilbake() {
        this.modus.set('skjema');
    }

    gaaTilFoersteUbesvarte() {
        const førsteUbesvarte = this.skjema.value
          .findIndex(innslag => erSpørsmålSomKanBesvares(innslag.spoersmaal!) && !SpørreundersøkelseSpørsmålSkjemaKomponent.erBesvart(innslag.svar));
        const spørsmål = this.el.nativeElement.querySelectorAll('spoerreundersoekelse-spoersmaal-skjema')[førsteUbesvarte] as HTMLElement;

        scrollTilSenter(spørsmål, 0);
        //vent til scrolling er ferdig
        setTimeout(() => {
            const blinkAnimasjon = this.blinkSpørsmålsrad.create(spørsmål);
            blinkAnimasjon.play();
            blinkAnimasjon.onDone(() => blinkAnimasjon.destroy());
        }, 500);
    }

    async registrer() {
        try {
            await this.lagrer.utfør(async () => {
                await this.avhengigheter.registrerBesvarelse(this.spoerreundersoekelse().kallendeBrukersRespondentId!, this.somSpesifikasjon());
                this.modus.set('kvittering');
            });

        } catch (feil) {
            if (feil instanceof HttpErrorResponse) {

                if (feil.status == HttpResponskoder.ValideringsfeilException) {
                    const feilbeskrivelse = feil.error as ValideringsresultatModell<RegistrerBesvarelseValideringsfeil>;
                    if (feilbeskrivelse.feil.some(feil => feil.feilkode == RegistrerBesvarelseValideringsfeil.undersøkelse_ikke_åpen)) {
                        this.gåTilStengtSteg();
                        return;
                    }

                }
                if (feil.status == HttpResponskoder.GONE) {
                    this.gåTilStengtSteg();

                }
            }
        }
    }

    visResultater() {
        this.navigasjon.gåTilGruppeStatistikk(this.spoerreundersoekelse().eiendeGruppeId);
        this.avbryt();
    }

    /** Lukker dialogen og returnerer false, med mindre vi står på kvitterings-steget*/
    avbryt() {
        this.dialogRef.close(this.modus() == 'kvittering');
    }

    ferdig() {
        this.dialogRef.close(true);
    }

    // private hjelpemetoder

    private async initSkjema(spørreundersøkelseId: SpørreundersøkelseId, respondentId: RespondentId | nil) {
        try {
            const spørreundersøkelse = await this.laster.utfør(() => this.avhengigheter.lastSpørreundersøkelse(spørreundersøkelseId));
            this.spoerreundersoekelse.set(spørreundersøkelse);
            this.gruppe.set(this.kontekst.autentisertBruker().finnAutentiserteGruppe(this.spoerreundersoekelse().eiendeGruppeId))
            if (!spørreundersøkelse.erAapenForRespons) {
                this.gåTilStengtSteg();
                return
            }
            SpørreundersøkelseBesvarelseSkjemaKomponent.populerSkjema(this.skjema, spørreundersøkelse);
            if (erIkkeNil(respondentId)) {
                const eksisterendeBesvarelse = await this.laster.utfør(() => this.avhengigheter.lastBesvarelse(respondentId));
                this.skjema.patchValue(this.skjema.value
                  .map(spørsmålsrad => ({svar: this.svarverdiSomStreng(spørsmålsrad, eksisterendeBesvarelse)})));
            }

        } catch (e) {
            //ved feil viser vi feilmelding og lukker dialogen
            this.meldingTilBruker.feilmelding(new I18nTekst(i18n('Kunne ikke hente data for spørreundersøkelsen')));
            this.avbryt();
        }
    }

    private svarverdiSomStreng(spørsmålsrad: SpørsmålSkjemaVerdi, eksisterendeBesvarelse: RespondentBesvarelseModell): Svarverdi {
        return SpørreundersøkelseSpørsmålSkjemaKomponent.svarStrengverdiSomSvarverdi(spørsmålsrad.spoersmaal!, eksisterendeBesvarelse.besvarelser[spørsmålsrad.spoersmaal!.id]);
    }

    private gåTilStengtSteg() {
        this.modus.set('stengt');
    }

    private somSpesifikasjon(): RegistrerRespondentsBesvarelseSpesifikasjon {
        const besvarelser = SpørreundersøkelseBesvarelseSkjemaKomponent.somSpesifikasjon(this.skjema.value)
          .filter(besvarelse => erIkkeNil(besvarelse.svar))
          .reduce((besvarelser, svar) => {
              besvarelser[svar.spørsmålId] = svar.svar!;
              return besvarelser;
          }, {} as Record<string, string>);
        return {besvarelser: besvarelser}
    }
}

export default SpørreundersøkelseBesvarelseDialogKomponent

@Injectable()
export class SpørreundersøkelseBesvarelsedialogKomponentAvhengigheter {

    constructor(private spørreundersøkelseRessurs: SpørreundersøkelseRessursClient) {
    }

    /** Laster spørreundersøkelse, kaller et ansvarlig for å håndtere feil */
    lastSpørreundersøkelse(id: SpørreundersøkelseId): Promise<SpørreundersøkelseModell> {
        return this.spørreundersøkelseRessurs.hent(id).somPromise();
    }

    /** Laster besvarelse, kaller et ansvarlig for å håndtere feil */
    lastBesvarelse(respondentId: RespondentId): Promise<RespondentBesvarelseModell> {
        return this.spørreundersøkelseRessurs.hentBesvarelse(respondentId).somPromise()
    }

    /** Registrerer besvarelse, kaller er ansvarlig for å håndtere feil, se {@link SpørreundersøkelseRessursClient#registrerBesvarelse} for feil som bør håndteres */
    async registrerBesvarelse(id: RespondentId, spesifikasjon: RegistrerRespondentsBesvarelseSpesifikasjon): Promise<void> {
        await this.spørreundersøkelseRessurs.registrerBesvarelse(id, spesifikasjon).somPromise();
    }
}


export interface DialogParametre {
    spørreundersøkelseId: SpørreundersøkelseId;
    respondentId?: RespondentId;
}
